/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 11th 2021, 12:17:53 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { schemaBuilder, FIELD_ACCEPTED_TYPES } from "lib/validation";
import _ from "lodash";
import { pipe } from "lodash/fp";
import { formatDateToISO8601 } from "lib/utils";

/**
 * This function as it returns the function to used to parse data based on the schema provided. So one can initiate it
 * with a schema and use it anywhere in their code.
 *
 * ⚠️ The multipartform parser has only been tested to work with the OneStop API based on Django REST with a minor customization
 * for handling lists in nested objects. Additionally, tests have only run with a nested object depth of 1 max.
 * We cant guarantee anything above that depth, so the API endpoints must go a to max depth of 1 for nested serializers.
 * The JSON data will work with any JSON request
 *
 *
 * @param dataSchema The Yup based validation schema for the data being parsed
 * @returns data in either a json parsed format or a custom parsed format for multipart form data that is
 * specifically structured for the Django Rest Framework
 *
 * @function parseAPIData: Automagically does the parsing. Not quite magic because it references the validation schema
 * to check for file based fields and returns multipart parsed data if file field found.
 *
 * @function isMultiPartForm: Checks the data object for any file fields as defined in schema.
 *
 * @function asMultiPart: Converts the data from javascript object to FormData. It also handles some
 * special fields differently when sending data as multipart data:
 *     @description
 *     1. Parsing a nested field for use with Django Rest Writable Nested fields in multipart
 *     form i.e. format is > order: [{price: 5, qty: 1}, {price: 10, qty: 1}] translates to individual values of
 *     order[0]price: 5, order[0]qty: 1, order[1]price: 10, order[0]qty: 1.
 *
 *     2. Parsing a List if items e.g. ids in for a multipart form {users: [1,2,3]} becomes users: 1, users: 2, users: 3
 *
 * @function asJSON: Converts data to JSON string.
 */

interface ParsingResult {
  parsedData: BodyInit;
  headers: { "Content-Type"?: string };
}

interface IAPIDataParser {
  parseAPIData(data: { [key: string]: any }): ParsingResult;
}

export const APIDataParser = (dataSchema: schemaBuilder.AnyObjectSchema): IAPIDataParser => {
  // Automagically parse the data
  const parseAPIData = (data: object): ParsingResult => {
    if (isMultiPartForm(data, dataSchema)) return pipe(parseByType, asMultiPart(dataSchema))(data);

    return pipe(asJSON)(data);
  };

  return { parseAPIData };
};

/** Parsing data types for Django backend */
const parseByType = (data: any) => {
  const newData = Object.keys(data).reduce((previous, current) => {
    const dateValue = _.isDate(data[current]) ? formatDateToISO8601(data[current]) : data[current];
    return { ...previous, [current]: dateValue };
  }, {});

  return newData;
};

const asJSON = (data: object): ParsingResult => ({
  parsedData: JSON.stringify(data),
  headers: { "Content-Type": "application/json" },
});

const isMultiPartForm = (data: object, dataSchema: schemaBuilder.AnyObjectSchema) => {
  // We do not check that the data contains a file field i.e. File, because django rest framework
  // will not accept the string representation of the saved file on edits. It only strictly accepst file
  // objects for saving to a FileField so we omit the string rep in asMultiPart.
  return Object.keys(data).some((field_key) => isFileField(field_key, dataSchema));
};

const asMultiPart = (dataSchema: schemaBuilder.AnyObjectSchema): ((data: object) => ParsingResult) => {
  return (data: object) => {
    const formData = new FormData();

    const cleaned_data: object = _(data).omitBy(_.isUndefined).value();

    const parseNestedItem = (nested_item: object, nested_item_key: string, nested_index: number, field_key: string) => {
      if (Array.isArray(nested_item[nested_item_key])) {
        return nested_item[nested_item_key].map((arrayValue, index) => {
          return { parsedKey: `${field_key}[${nested_index}]${nested_item_key}[${index}]`, parsedValue: arrayValue };
        });
      } else {
        return [
          { parsedKey: `${field_key}[${nested_index}]${nested_item_key}`, parsedValue: nested_item[nested_item_key] },
        ];
      }
    };

    Object.keys(cleaned_data).forEach((field_key) => {
      let field_value = cleaned_data[field_key];

      if (field_value === null) field_value = ""; // We still need to pass null values and MultipPart converts null to the string "null". So we convert it to an empty string which is translated to None in Django. This may be different for other frameworks

      // If file field does not have file, value, omit it from form data
      // TODO: test & figure out how to make this still work if a file field is required during edit
      if (isFileField(field_key, dataSchema) && !isFileValue(field_value)) return;

      // Parsing the list of primary keys for use in multipart form
      if (isMany2ManyField(field_key, dataSchema)) {
        field_value.forEach((pk) => formData.append(field_key, pk));
        return;
      }

      // Parsing a nested field for use with Django Rest Writable Nested fields in multipart form
      if (isNestedOne2ManyField(field_key, dataSchema)) {
        // field_value is a list of nested data items
        field_value.forEach((item, nested_index) =>
          Object.keys(item).forEach((item_key) => {
            const parsedKeyValuePairs = parseNestedItem(item, item_key, nested_index, field_key);
            parsedKeyValuePairs.forEach(({ parsedKey, parsedValue }) => formData.append(parsedKey, parsedValue));
          })
        );
        return;
      }

      // Parse JSON values for Multipart input
      // Comment for backend devs to note: Django Rest currently has a bug that requires one to
      // explicity set a serializer field as JSONField even if it has already been set in the model
      if (typeof field_value === "object" && !isFileField(field_key, dataSchema)) {
        formData.append(field_key, JSON.stringify(field_value));
        return;
      }

      if (isFileListValue(field_value) || isFileArrayValue(field_value)) {
        for (let i = 0; i < field_value.length; i++) {
          formData.append(field_key, field_value[i]);
        }
        return;
      }

      formData.append(field_key, field_value);
    });

    return { parsedData: formData, headers: {} }; // Let the browser determine the headers for multiform data, browser will also automatically add the multipart boundary parameter in the header
  };
};

// Field functions
const isFileField = (field_key: string, dataSchema: schemaBuilder.AnyObjectSchema) =>
  dataSchema.fields?.[field_key]?.describe().meta?.type === FIELD_ACCEPTED_TYPES.file;

const isFileValue = (value: any) => value instanceof File || isFileListValue(value) || isFileArrayValue(value);

const isFileListValue = (value: any) => value instanceof FileList;

// TODO: Check the security implications of allowing arrays of files that can be modified by user. Javascript made
// FileList immutable for a reason:
// https://developer.mozilla.org/en-US/docs/Web/API/FileList
// https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer/files
const isFileArrayValue = (value: any) => Array.isArray(value) && value.every((item) => item instanceof File);

const isMany2ManyField = (field_key: string, dataSchema: schemaBuilder.AnyObjectSchema) =>
  dataSchema.fields?.[field_key]?.describe().meta?.type === FIELD_ACCEPTED_TYPES.many2many;

const isNestedOne2ManyField = (field_key: string, dataSchema: schemaBuilder.AnyObjectSchema) =>
  dataSchema.fields?.[field_key]?.describe().meta?.type === FIELD_ACCEPTED_TYPES.nestedone2many;
